import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router, UrlSegment } from '@angular/router';
import {
  IBreadcrumb,
  IBreadcrumbConfig,
  IBreadcrumbDisplayProperties,
  IBreadcrumbDynamicConfig,
  ICombinedBreadcrumbValue,
} from '@interfaces/breadcrumb.interface';
import { filter, map, Observable, of, Subscription } from 'rxjs';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs: IBreadcrumb[];
  private routerEventsSub: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly injector: Injector,
  ) {}

  ngOnInit(): void {
    this.routerEventsSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
    }
  }

  private buildBreadcrumbs(route: ActivatedRoute, breadcrumbs: IBreadcrumb[] = []): IBreadcrumb[] {
    if (route.routeConfig?.title) {
      const combinedBreadcrumbs = [
        ...breadcrumbs,
        {
          label: route.title,
          path: this.getPath(route, [], 1),
        },
      ];
      return route.firstChild ? this.buildBreadcrumbs(route.firstChild, combinedBreadcrumbs) : combinedBreadcrumbs;
    } else if (route.routeConfig?.data?.breadcrumbs) {
      const routeBreadcrumbs = this.getRouteBreadcrumbs(route);
      const combinedBreadcrumbs = [...breadcrumbs, ...routeBreadcrumbs];
      return route.firstChild ? this.buildBreadcrumbs(route.firstChild, combinedBreadcrumbs) : combinedBreadcrumbs;
    } else if (route.firstChild) {
      return this.buildBreadcrumbs(route.firstChild, breadcrumbs);
    }
    return breadcrumbs;
  }

  private getRouteBreadcrumbs(route: ActivatedRoute): IBreadcrumb[] {
    return route.routeConfig.data.breadcrumbs.map((breadcrumbConfig: IBreadcrumbConfig, index: number) => ({
      label: this.getLabel(breadcrumbConfig, route.snapshot.params),
      path: breadcrumbConfig.isDisabled
        ? null
        : this.getPath(route, [], breadcrumbConfig.takenUrlSegmentsNumber || index + 1, breadcrumbConfig),
    }));
  }

  private getLabel(breadcrumbConfig: IBreadcrumbConfig | null, routeParams: Params): Observable<string> {
    if (breadcrumbConfig.isStatic) {
      return of(breadcrumbConfig.value);
    }
    return this.getDynamicLabel(breadcrumbConfig.config, routeParams);
  }

  private getPath(
    route: ActivatedRoute,
    paths: string[] = [],
    range: number,
    breadcrumbConfig?: IBreadcrumbConfig,
  ): string[] {
    const routeUrls = route.snapshot.url;
    const newPaths = this.getPaths(routeUrls, range);
    const result = [...newPaths, ...paths];
    if (route.parent.snapshot.url.length) {
      const newRange = (breadcrumbConfig && breadcrumbConfig.takenUrlSegmentsNumber) || 1;
      return this.getPath(route.parent, result, newRange, breadcrumbConfig);
    }
    return result;
  }

  private getPaths(routeUrls: UrlSegment[], range = 1): string[] {
    const paths = [];
    for (let i = 0; i < range; i++) {
      if (routeUrls[i]) {
        paths.push(routeUrls[i].path);
      }
    }
    return paths;
  }

  // TODO refactor
  private getDynamicLabel(config: IBreadcrumbDynamicConfig, routeParams: Params) {
    const service = this.injector.get(config.replaceBy.service);
    const methodName = config.replaceBy.method;
    const routeParamsForServiceCall: string[] = Array.isArray(config.toReplace) ? config.toReplace : [config.toReplace];
    const methodArguments = routeParamsForServiceCall.map((el: string) => routeParams[el]);
    const method = service[methodName](...methodArguments);

    return method.pipe(
      filter(Boolean),
      map((recordData: { [k: string]: unknown }) => {
        const propertiesToDisplay = config.replaceBy.displayProperties;
        const fallbackDisplayValue = config.replaceBy.fallbackValue;
        const displayValues = this.getProperties(propertiesToDisplay, recordData, fallbackDisplayValue);
        return displayValues.join(' ');
      }),
    );
  }

  private getProperties(
    properties: IBreadcrumbDisplayProperties,
    obj: {
      [k: string]: any;
    },
    fallbackDisplayValue = '',
  ): string[] {
    if (Array.isArray(properties)) {
      const propeprtiesToDisplay: any[] = properties.map((property: string | ICombinedBreadcrumbValue) =>
        typeof property === 'string' ? obj[property] : property.value,
      );
      return propeprtiesToDisplay.some((el) => !!el || el === 0) ? propeprtiesToDisplay : [fallbackDisplayValue];
    }
    const propsToDisplay: any[] = [];
    Object.keys(properties).forEach((key) => {
      properties[key].forEach((el: string) => {
        propsToDisplay.push(obj[key][el]);
      });
    });
    return propsToDisplay.some((el) => !!el || el === 0) ? propsToDisplay : [fallbackDisplayValue];
  }
}
